/**
 * External dependencies.
 */
import type { GetStaticProps, GetStaticPaths, NextPage } from "next";
import { useQuery } from "react-query";

/**
 * Internal dependencies.
 */
import Layout from "layout/layout";
import ArticleIntro from "components/article-intro/article-intro";
import Article from "blocks/article/article";
import PopularArticles from "blocks/popular-articles/popular-articles";
import { fetchAPI, getLocale, getSlug, loadTranslation } from "utils";
import { MediaProp } from "types";
import { ArticleTagsProps } from "types/article";
import SEO from "components/seo/seo";

interface BlogPostAttrProps {
  Author: String;
  Content: String;
  Image: MediaProp;
  ReadTime: String;
  Title: String;
  article_tags: ArticleTagsProps;
  createdAt: Date;
  slug: String;
  SEO: any;
}

interface BlogPostData {
  id: number;
  attributes: BlogPostAttrProps;
}

interface BlogPostProps {
  data: BlogPostData;
}

const BlogPost: NextPage<BlogPostProps> = ({ data }) => {
  const {
    id: articleId,
    attributes: {
      Title,
      Content,
      ReadTime,
      article_tags: tags,
      Image,
      createdAt,
      Author,
      SEO: seoData,
    },
  } = data;

  useQuery(
    ["articleCountView", articleId],
    async () => {
      await fetchAPI("/article/countView", {
        articleId,
      });
    },
    {
      refetchOnWindowFocus: false,
    }
  );

  const popularArticles = useQuery(["popularArticles", articleId], async () => {
    let popularArticlesData: any = [];

    await fetchAPI("/article/getPopular", {
      articleId,
    }).then((data) => (popularArticlesData = data.popularArticles));

    return popularArticlesData;
  });

  return (
    <Layout
      title="Blog"
      desc="DigiFist"
      light={false}
      dark={false}
      FooterAlt={true}
      notice
    >
      <SEO data={seoData} />

      <ArticleIntro
        title={Title}
        tags={tags}
        readTime={ReadTime}
        imgData={Image}
        createdAt={createdAt}
        author={Author}
      />

      <Article content={Content} />

      {popularArticles?.data && popularArticles.data.length && (
        <PopularArticles articles={popularArticles} />
      )}
    </Layout>
  );
};

export const getStaticProps: GetStaticProps = async (ctx) => {
  const translation = await loadTranslation(
    ctx.locale!,
    process.env.NODE_ENV === "production"
  );
  const locale = getLocale(ctx.locale);

  const { data } = await fetchAPI("/articles", {
    populate: {
      Image: "*",
      article_tags: "*",
      SEO: {
        populate: "*",
      },
      localizations: "*",
    },
    locale,
    filters: {
      $or: [
        {
          slug_overwrite: ctx.params?.slug,
        },
        {
          slug: ctx.params?.slug,
        },
      ],
    },
  });

  return {
    props: {
      translation,
      data: data.pop(),
    },
    revalidate: 10,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  let paths: any = [];
  const { data } = await fetchAPI("/articles", {
    populate: "*",
  });
  data?.map(({ attributes }: any) => {
    if (attributes.localizations.data.length) {
      attributes.localizations.data.map((item: any) => {
        paths.push({
          params: { slug: getSlug(item.attributes) },
          locale: item.attributes.locale?.toLowerCase(),
        });
      });
    }
    paths.push({
      params: { slug: getSlug(attributes) },
      locale: attributes.locale?.toLowerCase(),
    });
  });

  return {
    paths,
    fallback: false,
  };
};

export default BlogPost;
